import React, {useCallback, useMemo} from 'react';
import {GatewayGetSchema} from '../../../../../api/DBSchema/Gateway';
import {useTranslation} from 'react-i18next';
import PlanEditorInnerContainer from '../../../../../components/Editor/PlanEditorInnerContainer';
import Separator from '../../../../../components/Simple/Sepperator';
import SMCLabel from '../../../../../components/Simple/SMCLabel';
import EmptyRow from '../../../../../components/Simple/EmptyRow';
import EditorLayout from '../../../../../components/Layouts/EditorLayout';
import {SMCSelect, SMCOption} from '../../../../../components/Simple/SMCSelect';
import SMCIcon from '../../../../../components/Simple/SMCIcon';
import {SMCRadioGroup, SMCRadio} from '../../../../../components/Simple/SMCRadio';
import RowLayout from '../../../../../components/Layouts/RowLayout';
import {SMCToggleSwitch} from "../../../../../components/Simple/SMCToggleSwitch";

const pageSchema = GatewayGetSchema.pages.unifiedThreatPrevention;
const pageId = pageSchema.pageId;

function BaseUnifiedThreatPrevention({
    pageData,
    setObjectData,
    pageDisabled,
    setEditorPageId
}) {
    const { t } = useTranslation();

    const manageInSmp       = pageData[pageSchema.manageInSmp];
    const policy            = pageData[pageSchema.policy];
    const highConfidence    = pageData[pageSchema.highConfidence];
    const mediumConfidence  = pageData[pageSchema.mediumConfidence];
    const lowConfidence     = pageData[pageSchema.lowConfidence];
    const performanceImpact = pageData[pageSchema.performanceImpact];
    const track             = pageData[pageSchema.track];
    const severity          = pageData[pageSchema.severity];

    const policyValues            = pageSchema.policyValues;
    const confidenceValues        = pageSchema.confidenceValues;
    const performanceImpactValues = pageSchema.performanceImpactValues;
    const trackValues             = pageSchema.trackValues;
    const severityValues          = pageSchema.severityValues;

    const setPageValue = useCallback((param, newValue) => {
        setObjectData([pageId, param], newValue);
    }, [setObjectData]);

    const confidenceOptions = useMemo(() => (
        <>
        <SMCOption value={confidenceValues.ask}>{t('SMP.PLAN.TP.ASK')}</SMCOption>
        <SMCOption value={confidenceValues.prevent}>{t('SMP.PLAN.TP.PREVENT')}</SMCOption>
        <SMCOption value={confidenceValues.detect}>{t('SMP.PLAN.TP.DETECT')}</SMCOption>
        <SMCOption value={confidenceValues.inactive}>{t('SMP.PLAN.TP.INACTIVE')}</SMCOption>
        </>
    ), [confidenceValues, t]);


    return (
        <EditorLayout>
            <SMCToggleSwitch
                disabled={pageDisabled}
                checked={manageInSmp}
                onChange={checked => setPageValue(pageSchema.manageInSmp, checked)}
                label={t('SMP.PLAN.MANAGE_IN_SMP')}
            />
            <Separator isMainSeparator/>
            <PlanEditorInnerContainer title={t('SMP.PLAN.UNIFIED_THREAT_PREV_POLICY')} managedInSmp={manageInSmp}>
                <RowLayout spacing="8px">
                    <SMCIcon icon="info" />
                    <span>
                        <SMCLabel>{t('SMP.PLAN.TP.INFO_COL1')}</SMCLabel>
                        <SMCLabel>{t('SMP.PLAN.TP.INFO2')} <span className="Link" onClick={() => setEditorPageId(GatewayGetSchema.pages.threatPrevention.pageId)}> {t('SMP.PLAN.THREAT_PREV_POLICY')}</span></SMCLabel>
                    </span>
                </RowLayout>

                <EmptyRow />
                
                {/* Policy */}
                <SMCRadioGroup
                    disabled={pageDisabled || !manageInSmp}
                    value={policy}
                    onChange={e => setPageValue(pageSchema.policy, e.target.value)}
                >
                    <SMCRadio value={policyValues.strict} label={t('SMP.PLAN.TP.STRICT')} />
                    <SMCRadio value={policyValues.recommended} label={t('SMP.PLAN.TP.RECOMMENDED')} />
                    <SMCRadio value={policyValues.custom} label={t('SMP.PLAN.TP.CUSTOM')} />
                </SMCRadioGroup>
                {/* Tracking options */}
                <SMCSelect
                    disabled={pageDisabled || !manageInSmp}
                    value={track}
                    onChange={e => setPageValue(pageSchema.track, e.target.value)}
                    label={t('SMP.PLAN.TP.TRACKING_OPTIONS')+':'}
                >
                    <SMCOption value={trackValues.log}>{t('SMP.PLAN.TP.LOG')}</SMCOption>
                    <SMCOption value={trackValues.alert}>{t('SMP.PLAN.TP.ALERT')}</SMCOption>
                    <SMCOption value={trackValues.none}>{t('SMP.PLAN.TP.NONE')}</SMCOption>
                </SMCSelect>

                {
                    policy === policyValues.custom && (
                        <>
                        <EmptyRow />
                        <SMCLabel bold>{t('SMP.PLAN.TP.PROTECTION_ACTIVATION')}</SMCLabel>
                        {/* High confidence */}
                        <SMCSelect
                            disabled={pageDisabled || !manageInSmp}
                            value={highConfidence}
                            onChange={e => setPageValue(pageSchema.highConfidence, e.target.value)}
                            label={t('SMP.PLAN.TP.HIGH_CONF')+':'}
                        >
                            {confidenceOptions}
                        </SMCSelect>
                        {/* Medium confidence */}
                        <SMCSelect
                            disabled={pageDisabled || !manageInSmp}
                            value={mediumConfidence}
                            onChange={e => setPageValue(pageSchema.mediumConfidence, e.target.value)}
                            label={t('SMP.PLAN.TP.MED_CONF')+':'}
                        >
                            {confidenceOptions}
                        </SMCSelect>
                        {/* Low confidence */}
                        <SMCSelect
                            disabled={pageDisabled || !manageInSmp}
                            value={lowConfidence}
                            onChange={e => setPageValue(pageSchema.lowConfidence, e.target.value)}
                            label={t('SMP.PLAN.TP.LOW_CONF')+':'}
                        >
                            {confidenceOptions}
                        </SMCSelect>
                        {/* Severity */}
                        <SMCSelect
                            disabled={pageDisabled || !manageInSmp}
                            value={severity}
                            onChange={e => setPageValue(pageSchema.severity, e.target.value)}
                            label={t('SMP.PLAN.TP.SEVERITY')+':'}
                        >
                            <SMCOption value={severityValues.low}>{t('SMP.PLAN.TP.LOW_OR_ABOVE')}</SMCOption>
                            <SMCOption value={severityValues.medium}>{t('SMP.PLAN.TP.MEDIUM_OR_ABOVE')}</SMCOption>
                            <SMCOption value={severityValues.high}>{t('SMP.PLAN.TP.HIGH_OR_ABOVE')}</SMCOption>
                            <SMCOption value={severityValues.critical}>{t('SMP.PLAN.TP.CRITICAL')}</SMCOption>
                        </SMCSelect>
                        {/* Performance impact */}
                        <SMCSelect
                            disabled={pageDisabled || !manageInSmp}
                            value={performanceImpact}
                            onChange={e => setPageValue(pageSchema.performanceImpact, e.target.value)}
                            label={t('SMP.PLAN.TP.PERFORMANCE_IMPACT')+':'}
                        >
                            <SMCOption value={performanceImpactValues.low}>{t('SMP.PLAN.TP.LOW')}</SMCOption>
                            <SMCOption value={performanceImpactValues.medium}>{t('SMP.PLAN.TP.MED_OR_LOWER')}</SMCOption>
                            <SMCOption value={performanceImpactValues.high}>{t('SMP.PLAN.TP.HIGH_OR_LOWER')}</SMCOption>
                        </SMCSelect>

                        </>
                    )
                }

            </PlanEditorInnerContainer>
        </EditorLayout>
    );
}

export default BaseUnifiedThreatPrevention;